import {
  ActionCenterTypes,
  ActionResponseStatus,
  Nullable,
  Targets,
} from '@/core/interfaces/common';

import {
  EmailThankYouMessage,
  FormHeading,
  OfficialWrapper,
  TalkingPointItem,
  TalkingPointsHeading,
  TalkingPointsList,
  TargetCard,
  TargetsWrapper,
  ThankYouMessage,
} from '@/themes/globalStyles';

import { ThankYouWrapper } from '../ThankYouWrapper';

interface ActionTakenComponentProps {
  actionMode: ActionResponseStatus | undefined;
  actionCenterType: ActionCenterTypes;
  backgroundColor: string;
  buttonColor: string;
  isDonateButton: boolean;
  isFullScreen?: boolean;
  linkDonate: Nullable<string>;
  multipleMessages: boolean;
  talkingPoints: string;
  targets: Targets;
  thankYouMessage: string;
  socialShareButtonEnabled: boolean;
  submitError: string | undefined;
  textColor: string;
  uuid: string | Array<string> | undefined;
}

export const ActionTakenComponent = ({
  actionCenterType,
  actionMode,
  backgroundColor,
  buttonColor,
  linkDonate,
  multipleMessages,
  isDonateButton,
  isFullScreen = false,
  targets,
  talkingPoints,
  thankYouMessage,
  socialShareButtonEnabled,
  submitError,
  textColor,
  uuid,
}: ActionTakenComponentProps) => {
  const {
    emailType,
    phoneType,
  } = ActionCenterTypes;

  const emailSuccessfullyMessage = multipleMessages ? 'Your messages were sent to' : 'Your message was sent to:';
  const emailActionTaken = multipleMessages ? 'Messages sent successfully' : 'Message sent successfully!';
  /**
   * Returns the header text labeling the officials.
   */
  let targetsFormHeader: string;
  let actionTakenHeader: string;

  if (actionMode === ActionResponseStatus.test) {
    targetsFormHeader = 'Your messages would have been sent to:';
    actionTakenHeader = 'Test message submitted!';
  } else if (actionMode === ActionResponseStatus.petition) {
    targetsFormHeader = 'Your signature has been added for:';
    actionTakenHeader = 'Signature added!';
  } else {
    targetsFormHeader = actionCenterType === phoneType ? 'Your call will be connected to:' : emailSuccessfullyMessage;
    actionTakenHeader = actionCenterType === phoneType ? 'Your phone call will start soon!' : emailActionTaken;
  }
  /**
   * Returns rendered officials targets included in the action.
   */
  const renderTargets = () => (
    /**
     * Do not show targets for petitions!
     */
    !!targets.length && actionMode !== ActionResponseStatus.petition &&
      (
        <>
          <FormHeading>{targetsFormHeader}</FormHeading>
          <TargetsWrapper>
            {targets.map(target => (
              <TargetCard
                backgroundcolor={backgroundColor}
                key={`${target.firstName} ${target.lastName}`}
              >
                <strong>{`${target.firstName} ${target.lastName}`}</strong>
                <span>{target.office}</span>
              </TargetCard>
            ))}
          </TargetsWrapper>
        </>
      )
  );

  return (
    <ThankYouWrapper
      borderColor={backgroundColor}
      buttonColor={buttonColor}
      isDonateButton={isDonateButton}
      isFullScreen={isFullScreen}
      linkDonate={linkDonate}
      name={submitError || actionTakenHeader}
      socialShareButtonEnabled={socialShareButtonEnabled}
      textcolor={textColor}
      uuid={uuid}
    >
      {!submitError && (
        <>
          {actionCenterType === emailType ?
            (
              <>
                <OfficialWrapper>
                  {renderTargets()}
                </OfficialWrapper>
                {thankYouMessage && (
                  <EmailThankYouMessage
                    // eslint-disable-next-line react/no-danger
                    dangerouslySetInnerHTML={{
                      __html: thankYouMessage,
                    }}
                  />
                )}
              </>
            ) :
            (
              <>
                <TalkingPointsHeading>Call taking points</TalkingPointsHeading>
                <TalkingPointsList>
                  {talkingPoints.split('\n').map(talkingPoint => (
                    <TalkingPointItem
                      backgroundcolor={backgroundColor}
                      key={talkingPoint}
                    >
                      {talkingPoint}
                    </TalkingPointItem>
                  ))}
                </TalkingPointsList>
                <OfficialWrapper>
                  {renderTargets()}
                </OfficialWrapper>
                <FormHeading>Thank You for taking action.</FormHeading>
                {thankYouMessage && (
                  <ThankYouMessage
                    // eslint-disable-next-line react/no-danger
                    dangerouslySetInnerHTML={{
                      __html: thankYouMessage,
                    }}
                  />
                )}
              </>
            )
          }
        </>
      )}
    </ThankYouWrapper>
  );
};
